<script lang="ts" setup>
import type { DataLayerItem } from '~layers/app/composables/useDataLayer'

const { setBreadcrumbs /* previousPage */ } = useBreadcrumbs()
const { search } = useCatalogSearch()
const route = useRoute()
const { $t } = useI18n()

const meta = inject('meta', ref())

setBreadcrumbs({
  label: meta.value.label,
  parents: meta.value.parents?.filter(parent => !!parent),
})

const {
  products,
  fetchProductData,
} = useCatalog(meta)

const {
  page,
  totalPages,
  sort,
  sortOptions,
  itemsPerPage,
  // itemsPerPageOptions,
  view,
  totalCount,
  paginatorMethods,
} = useCatalogPaginator()

const { strapiData, getStrapiData } = useStrapiCms<StrapiPlpPage>(
  meta?.value?.strapi_id || '',
  'PUBLISHED',
  (meta?.value?.page_type === 'plp-theme' ? 'plpThemePage' : 'plpPage'),
  '',
)

await Promise.all([
  fetchProductData(),
  getStrapiData(),
])

const plpPage = computed(() => {
  return strapiData.value?.plpPage
})

const pageTitle = computed(
  () => meta.value?.label,
)

const metaTitle = computed(
  () => plpPage.value?.MetaData?.MetaTitle || pageTitle.value + ' | ' + $t('meta.title'),
)

const metaDescription = computed(
  () => plpPage.value?.MetaData?.MetaDescription || $t('meta.plp.description', { name: pageTitle.value }),
)

const plpUsps = computed(() => {
  return plpPage.value?.PLPData?.Usp?.Usp || strapiData.value?.plpUsps?.[0]?.Usp
})

const plpBanner = computed(() => {
  return plpPage.value?.PLPData?.Banner
})

const plpQuicklinks = computed(() => {
  return plpPage.value?.PLPData?.Quicklinks?.Icons
})

const plpGridBanner1 = computed(() => {
  return plpPage.value?.PLPData?.GridBanner1
})

const plpGridBanner2 = computed(() => {
  return plpPage.value?.PLPData?.GridBanner2
})

const plpGridBanner3 = computed(() => {
  return plpPage.value?.PLPData?.GridBanner3
})

const { pageView, viewItemList } = useDataLayer()
pageView('overview', pageTitle.value)

const dataLayerOverrides = computed<Partial<DataLayerItem>>(() => ({
  item_list_name: 'plp',
  item_list_id: route.fullPath,
}))

const category = computed<Partial<MagentoCategory>>(() => ({
  id: meta.value?.id?.split('-')?.[1],
  name: meta.value?.label,
}))

watch(products, () => {
  viewItemList(products.value, dataLayerOverrides.value, category.value)
}, { immediate: true })

const metaRobots = computed(() => {
  const { p, filter, color, ...restOfQuery } = route.query
  if (Object.keys(restOfQuery).length > 0) {
    return 'noindex, follow'
  }
  if (plpPage.value?.MetaData?.MetaRobots) {
    return plpPage.value.MetaData.MetaRobots.replace('_', ', ')
  }
  return 'index, follow'
})

useHead({
  title: metaTitle,
  meta: [
    { key: 'description', name: 'description', content: metaDescription },
    { key: 'robots', name: 'robots', content: metaRobots },
  ],
})

const canonicalUrl = computed(() => {
  if (metaRobots.value.startsWith('index')) {
    return route.fullPath
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { filter, color, ...restOfQuery } = route.query
  const searchParams = new URLSearchParams({
    ...(color ? { filter, color } : {}),
  })
  return route.path + (searchParams.size > 0 ? '?' + searchParams.toString() : '')
})

const { canonical } = useSeo()
canonical(canonicalUrl)
</script>

<template>
  <div class="plp">
    <StrapiBanner
      v-if="plpBanner"
      class="plp__banner banner--hero"
      :banner="plpBanner"
      :lazy="false"
    />

    <AppBreadcrumbs v-if="$route.path !== '/'" />

    <div class="plp__container">
      <div class="plp__layout">
        <div class="plp__heading">
          <h1>
            <template v-if="search">
              {{ $t('Search results for') }}: '{{ search }}'
            </template>
            <template v-else>
              {{ pageTitle }}
            </template>
          </h1>
          <span class="count">({{ totalCount }})</span>
        </div>

        <CatalogQuickLinks
          v-if="plpQuicklinks?.length"
          class="plp__quick-links"
          :links="plpQuicklinks"
        />

        <FilterList class="plp__filters" />

        <PaginatorToolbar
          class="plp__paginator plp__paginator--top"
          :sort="sort"
          :sort-options="sortOptions"
          v-on="paginatorMethods"
        />

        <div
          id="product-grid"
          class="plp__products products"
          :class="`products--${view}`"
        >
          <ProductTile
            v-for="(product, index) in products"
            :key="index"
            :data-layer-overrides="dataLayerOverrides"
            :product="product"
            list="ProductListerPage"
          />

          <AppUsps
            v-if="plpUsps?.length && page === 1 && products.length >= 3"
            class="plp__usps"
            :usps="plpUsps"
            :horizontal="true"
          />

          <StrapiBanner
            v-if="plpGridBanner1 && page === 1 && products.length >= 4"
            class="plp__grid-banner plp__grid-banner--1"
            :banner="plpGridBanner1"
            :lazy="true"
          />

          <StrapiBanner
            v-if="plpGridBanner2 && page === 1 && products.length >= 6"
            class="plp__grid-banner plp__grid-banner--2"
            :banner="plpGridBanner2"
            :lazy="true"
          />

          <CatalogGridBanner
            v-if="plpGridBanner3 && page === 1 && products.length >= 12"
            class="plp__grid-banner plp__grid-banner--3"
            :banner="plpGridBanner3"
            :lazy="true"
          />
        </div>

        <PaginatorToolbar
          class="plp__paginator plp__paginator--bottom"
          :current-page="page"
          :total-pages="totalPages"
          :items-per-page="itemsPerPage"
          v-on="paginatorMethods"
        />

        <UiReadMore
          v-if="plpPage?.PLPData?.Description"
          class="plp__description"
        >
          <StrapiBlocksText
            :nodes="plpPage.PLPData?.Description"
          />
        </UiReadMore>
      </div>

      <StrapiSlices
        v-if="plpPage?.Slices?.length"
        class="plp__content"
        :slices="plpPage.Slices"
      />

      <UiScrollToTop class="plp__scroll-to-top" />
    </div>

    <ThirdpartySqueezelyProductSlider id="spotler-activate-plp-slider-1" />
    <ThirdpartySqueezelyProductSlider id="spotler-activate-plp-slider-2" />

    <SlicesMobileStickyButton
      v-if="plpPage?.MobileStickyButton"
      :button="plpPage.MobileStickyButton"
    />
  </div>
</template>

<style>
@import url("~layers/app/pages/~plp.css");
</style>
